/**
 * The counts the engine stops at.
 *
 * These are the numbers that decide whether the twenty-first siren light on
 * somebody's police car ever comes on. They are not configuration and they are
 * not opinion, and until now the repo held two different answers to the same
 * question in two different tools — 20 in the desktop analyzer, 32 in Blinklab —
 * with neither referencing the other and both shipping.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PROVENANCE — both answers were right, and this is why
 *
 * `SIREN_BONE_LIMIT` is 20, verified rather than remembered: every one of the 42
 * siren profiles in Rockstar's own `carcols.meta` and `carcols.ymt` on build
 * 1.0.3889.0 declares exactly 20 sirens, with no profile above or below. The
 * engine drives them from bones `siren1` through `siren20`, so a profile with
 * more entries has nothing to attach the extras to.
 *
 * `SIREN_BONE_LIMIT_WITH_SSLA` is 32, and it is not a contradiction: the Siren
 * Setting Limit Adjuster raises the cap on servers that load it. That is why a
 * tool that authors 32-light patterns is right to do so, and why a tool that
 * warns above 20 is also right — they are answering for different servers. A
 * warning about the limit has to say which one it means, or it reads as a bug in
 * whichever tool the user trusts less.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Sirens the base engine can drive, one per `siren1`..`siren20` bone.
 *
 * Verified against 42 stock siren profiles, all of which declare exactly this
 * many. See the header for why the number is not simply folklore.
 */
export const SIREN_BONE_LIMIT = 20;

/** The raised cap on a server running the Siren Setting Limit Adjuster. */
export const SIREN_BONE_LIMIT_WITH_SSLA = 32;

/**
 * The bone a siren index drives, for a message that names the actual bone.
 *
 * "The 24th light has no bone" is abstract; "there is no `siren24` bone on this
 * model" is something the author can go and look for in their model.
 */
export function sirenBoneName(index: number): string {
  return `siren${index}`;
}

/**
 * Whether a siren count is over the limit, and which limit it is over.
 *
 * Returns `null` when the count is fine on a stock server. Callers that know
 * their audience runs the limit adjuster pass `ssla: true` and only hear about
 * the higher ceiling.
 */
export function sirenCountVerdict(
  count: number,
  options: { ssla?: boolean } = {},
): { limit: number; over: number; needsSsla: boolean } | null {
  const ssla = options.ssla === true;
  const limit = ssla ? SIREN_BONE_LIMIT_WITH_SSLA : SIREN_BONE_LIMIT;
  if (count <= limit) return null;
  return {
    limit,
    over: count - limit,
    needsSsla: !ssla && count <= SIREN_BONE_LIMIT_WITH_SSLA,
  };
}

/**
 * Vehicle mods a kit can carry before the game stops reading them.
 *
 * UNVERIFIED. This one is community knowledge and it is not corroborated by the
 * stock files the way the siren limit is — Rockstar's own kits stay well under
 * it, which proves nothing about where the ceiling actually sits. It is here so
 * that a tool that wants to warn has one number to warn against, and it is
 * labelled so that nobody mistakes it for a measured fact.
 */
export const VISIBLE_MODS_SOFT_LIMIT = 255;
